
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import GeneralCard from '@/components/settings/identityIntegrations/GeneralCard.vue';
import AuthenticationCard from '@/components/settings/identityIntegrations/AuthenticationCard.vue';
import ScopeCard from '@/components/settings/identityIntegrations/ScopeCard.vue';
import AttributeMappingCard from '@/components/settings/identityIntegrations/AttributeMappingCard.vue';
import CommunityResolutionCard from '@/components/settings/identityIntegrations/CommunityResolutionCard.vue';
import JitCard from '@/components/settings/identityIntegrations/JitCard.vue';
import CertificateCard from '@/components/settings/identityIntegrations/CertificateCard.vue';
import LinkedIdentitiesPanel from '@/components/settings/identityIntegrations/LinkedIdentitiesPanel.vue';
import AuditLogPanel from '@/components/settings/identityIntegrations/AuditLogPanel.vue';
import IntegrationStatusPill from '@/components/settings/identityIntegrations/IntegrationStatusPill.vue';
import IntegrationSummary from '@/components/settings/identityIntegrations/IntegrationSummary.vue';
import PortalAccessCard from '@/components/settings/identityIntegrations/PortalAccessCard.vue';
import { collectErrors, errorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import { EPermissionActions } from '@/model/EPermissionActions';
import { apiErrorStatus, identityIntegrationsService, IIdentityIntegration } from '@/services/identityIntegrationsService';

/**
 * Four groups rather than one tab per card: the cards are not independent
 * settings, they are four questions. Who is this integration, how do we trust
 * it, how does its assertion become one of our users, and what has it done.
 * Splitting related cards apart made an operator hop between tabs to answer a
 * single question — the attribute mapping is meaningless without the Community
 * scope next to it.
 */
const SECTIONS = ['overview', 'connection', 'identity'];
const AUDIT_SECTION = 'activity';

const TAB_ICONS: { [section: string]: string } = {
  overview: 'mdi-view-dashboard-outline',
  connection: 'mdi-shield-key-outline',
  identity: 'mdi-account-switch-outline',
  activity: 'mdi-history',
};

const TAB_QUERY_PARAM = 'tab';

@Component({
  name: 'IdentityIntegration',
  components: {
    GeneralCard,
    AuthenticationCard,
    ScopeCard,
    AttributeMappingCard,
    CommunityResolutionCard,
    JitCard,
    CertificateCard,
    LinkedIdentitiesPanel,
    AuditLogPanel,
    IntegrationStatusPill,
    IntegrationSummary,
    PortalAccessCard,
  },
})
export default class IdentityIntegration extends Vue {
  @Prop({ required: true })
  protected integrationId!: number;

  protected loading: boolean = true;
  protected integration: IIdentityIntegration | null = null;
  protected notFound: boolean = false;
  protected loadError: string | null = null;
  protected tab: number = 0;

  created() {
    this.tab = this.tabFromQuery();
    this.fetch();
  }

  get canManage(): boolean {
    return this.$canAction(EPermissionActions.ACTION_IDENTITY_INTEGRATIONS_MANAGE);
  }

  get canAudit(): boolean {
    return this.$canAction(EPermissionActions.ACTION_IDENTITY_INTEGRATIONS_AUDIT);
  }

  get sections(): string[] {
    return this.canAudit ? SECTIONS.concat([AUDIT_SECTION]) : SECTIONS;
  }

  tabIcon(section: string): string {
    return TAB_ICONS[section];
  }

  /**
   * The open section lives in the URL so a tab can be linked to, reopened
   * after a refresh, and kept when the browser goes back — none of which a
   * purely local index survives.
   */
  @Watch('tab')
  onTabChanged(tab: number) {
    const section = this.sections[tab];

    if (!section || this.$route.query[TAB_QUERY_PARAM] === section) {
      return;
    }

    const query = Object.assign({}, this.$route.query, { [TAB_QUERY_PARAM]: section });

    // replace, not push: flipping between tabs is not navigation, and filling
    // the history with it would make the back button useless.
    this.$router.replace({ query }).catch(() => undefined);
  }

  async fetch() {
    this.loading = true;
    this.notFound = false;
    this.loadError = null;
    try {
      this.integration = await identityIntegrationsService.get(this.integrationId);
    } catch (error) {
      if (apiErrorStatus(error) === 404) {
        this.notFound = true;
      } else {
        this.loadError = errorMessage(this, collectErrors(error)[0]);
      }
    } finally {
      this.loading = false;
    }
  }

  onUpdated(updated: IIdentityIntegration) {
    this.integration = updated;
    this.fetch();
  }

  /**
   * An unknown or missing section falls back to the first tab rather than
   * showing nothing — a stale bookmark should still open the page.
   */
  private tabFromQuery(): number {
    const requested = this.$route.query[TAB_QUERY_PARAM];
    const index = this.sections.indexOf(Array.isArray(requested) ? String(requested[0]) : String(requested));

    return index === -1 ? 0 : index;
  }
}
